import { zodResolver } from '@hookform/resolvers/zod';
import { Lock } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import useApiRequest from '../../../lib/hooks/useRequest';
import { formatDateDetail, formatDateTime } from '../../../lib/hooks/utils';
import { InternalUserListResponseDto } from '../../../lib/interfaces/internal/users';
import { OnboardingStage, OrganizationDetailsResponseDto, UpdateOrganizationRequestDto } from '../../../lib/interfaces/organisation';
import { Button } from '../../../ui/components/ui/button';
import DataFetchParent from '../../../ui/components/ui/data-fetch';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../ui/components/ui/form';
import { H4, H5 } from '../../../ui/components/ui/headings';
import { Input } from '../../../ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/components/ui/select';
import AddOrganizationMemberForm from './add-organization-member';
import AssignmentCard from './components/assignment-card';
import OfferCheckoutCard from './components/offer-checkout-card';
import OrganisationInvitationsList from './organisation-invitations-list';
import OrganisationUserEditor from './organisation-user-editor';

const OrganisationDetailParent = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [organisation, setOrganisation] = useState<OrganizationDetailsResponseDto>();
  const [reload, setReload] = useState(false);
  const { apiRequest } = useApiRequest();

  const fetchOrganisation = useCallback(async () => {
    setIsLoading(true);
    const res = await apiRequest<OrganizationDetailsResponseDto>(`organizations/internal/${id}`, 'GET');
    setIsLoading(false);
    if (res.data) {
      setOrganisation(res.data);
    }
  }, [apiRequest, id]);

  useEffect(() => {
    fetchOrganisation();
  }, [fetchOrganisation, reload]);

  if (!id) {
    return <div>Organisation not found</div>;
  }

  return (
    <DataFetchParent
      data={organisation}
      renderElement={(data) => <OrganisationDetails organisationId={id} setReload={setReload} organisation={data} />}
      isLoading={isLoading}
    />
  );
};

/** Read only assignment state in the page header - editing happens in the assignment card at the bottom. */
const AssignmentSummary = ({ organisation }: { organisation: OrganizationDetailsResponseDto }) => {
  if (!organisation.multipleAssignmentEnabled) {
    return (
      <p className="text-[16px] text-[#888]">
        Ansprechpartner: {organisation.assignedInternalUserFullName ?? 'Nicht zugewiesen'}
        {organisation.assignedRepresentativeUserFullName && ` · Vertretung: ${organisation.assignedRepresentativeUserFullName}`}
      </p>
    );
  }

  const members = organisation.assignedInternalUsers ?? [];
  const carriers = members.filter((member) => member.capacity > 0);
  const representatives = members.filter((member) => member.capacity === 0);

  return (
    <p className="text-[16px] text-[#888]">
      Ansprechpartner:{' '}
      {carriers.length > 0 ? carriers.map((member) => `${member.fullName} (${member.capacity})`).join(', ') : 'Nicht zugewiesen'}
      {representatives.length > 0 && ` · Vertretung: ${representatives.map((member) => member.fullName).join(', ')}`}
    </p>
  );
};

const OrganisationDetails = ({
  organisation,
  organisationId,
  setReload
}: {
  organisation: OrganizationDetailsResponseDto;
  organisationId: string;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const isLocked = !!organisation.lockedByOfferId;
  const statusSchema = z
    .object({
      status: z.string().min(1, {
        message: 'Status ist erforderlich'
      }),
      statusDate: z.string().optional()
    })
    .refine(
      (data) => {
        if (selectedUsesDate(data.status)) {
          return data.statusDate !== '' && data.statusDate !== undefined && data.statusDate !== null;
        }
        return true;
      },
      {
        message: 'Statusdatum ist erforderlich',
        path: ['statusDate']
      }
    );

  type StatusSchema = z.infer<typeof statusSchema>;

  const getDateString = () => {
    const date = new Date(organisation.statusDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}-${month.toString().length === 1 ? '0' + month : month}-${day.toString().length === 1 ? '0' + day : day}`;
  };

  const form = useForm<StatusSchema>({
    resolver: zodResolver(statusSchema),
    defaultValues: {
      status: organisation.belongsToStatus,
      statusDate: organisation.statusDate !== null ? getDateString() : ''
    }
  });

  const updateStatus = async (data: StatusSchema) => {
    const req = {
      belongsToStatus: data.status,
      statusDate: data.statusDate ? new Date(data.statusDate).toISOString() : null
    };

    await apiRequest(`organizations/internal/${organisationId}/status`, 'PATCH', {
      body: req,
      toast: {
        toastText: 'Status erfolgreich aktualisiert'
      }
    });
    setReload((prev) => !prev);
  };

  const resetAdminPassword = async () => {
    await apiRequest(`organizations/internal/${organisationId}/resetAdminPassword`, 'POST', {
      toast: {
        toastText: 'Passwort erfolgreich zurückgesetzt'
      }
    });
  };

  const selectedUsesDate = (safeStatus: string) => {
    return organisation.selectableEntities?.status.find((status) => status.id === safeStatus)?.needsStatusDate;
  };

  const convertOnboardingStage = (stage: OnboardingStage) => {
    switch (stage) {
      case OnboardingStage.AddUsers:
        return 'Benutzer hinzufügen';
      case OnboardingStage.Done:
        return 'Abgeschlossen';
      case OnboardingStage.IntroductionVideo:
        return 'Einführungsvideo';
      default:
        return 'Nicht gestartet';
    }
  };

  const calculatePackagePrice = (packagePrice: number) => {
    let packagePriceString: string = packagePrice.toString();
    packagePriceString = packagePriceString.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    packagePriceString += '€/Monat';
    return packagePriceString;
  };

  return (
    <div className="flex flex-col gap-5 h-full">
      {/* <div className="flex flex-col gap-5 h-full"> */}
      {isLocked && (
        <div className="flex items-center gap-4 py-4 px-9 bg-white rounded-lg border border-[#5FD5DE] text-[16px] text-[#12282A]">
          <Lock className="h-5 w-5 shrink-0" />
          <p className="grow">
            Dieser Kunde wird bis zum Archivieren seines Angebots über das Angebot gepflegt – Änderungen dort unter „Vertragsdaten korrigieren“.
          </p>
          <Button asChild variant="outline" className="shrink-0">
            <Link to={`/offers?view=detail&id=${organisation.lockedByOfferId}`}>Zum Angebot</Link>
          </Button>
        </div>
      )}
      <div className="flex justify-between">
        <div>
          <h1 className="text-[32px] leading-[38px] font-bold text-[#12282A]">{organisation.name}</h1>
          <AssignmentSummary organisation={organisation} />
        </div>
      </div>
      {/* A disabled fieldset does not stop pointerdown (Radix Select opens on it) or clicks on plain elements, hence pointer-events-none. */}
      <fieldset disabled={isLocked} className={`flex flex-col gap-5 h-full min-w-0 ${isLocked ? 'pointer-events-none' : ''}`}>
        <div className="flex flex-row gap-5">
          <div
            className={`flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px] ${!organisation.selfCheckoutData && !organisation.offerCheckoutData ? 'w-full' : 'w-3/4'}`}>
            <H4>Daten zum Unternehmen</H4>
            {mode === 'view' && (
              <>
                <div className="grid grid-cols-3">
                  <p className="text-[16px] text-[#888]">Kundennummer: {organisation.customerNumber}</p>
                  <p className="text-[16px] text-[#888]">Debitreferenz: {organisation.debitReference}</p>
                  <p className="text-[16px] text-[#888]">Signaturdatum: {formatDateDetail(organisation.signatureDate)}</p>
                  <p className="text-[16px] text-[#888]">USt-IdNr.: {organisation.vatId}</p>
                  <p className="text-[16px] text-[#888]">Straße: {organisation.street}</p>
                  <p className="text-[16px] text-[#888]">Hausnummer: {organisation.houseNumber}</p>
                  <p className="text-[16px] text-[#888]">PLZ: {organisation.postCode}</p>
                  <p className="text-[16px] text-[#888]">Ort: {organisation.city}</p>
                  <p className="text-[16px] text-[#888]">Land: {organisation.country}</p>
                  <p className="text-[16px] text-[#888]">Rechnungsmail: {organisation.invoiceMail}</p>
                  <p className="text-[16px] text-[#888]">Erlaubte Jobsanzahl: {organisation.allowedJobsCount}</p>
                  <p className="text-[16px] text-[#888]">Onboarding Stage: {convertOnboardingStage(organisation.onboardingStage)}</p>
                  <p className="text-[16px] text-[#888]">IBAN: {organisation.iban}</p>
                  <p className="text-[16px] text-[#888]">BIC: {organisation.bic}</p>
                  <p className="text-[16px] text-[#888]">Telefon: {organisation.phone}</p>
                </div>

                <p className="text-[16px] text-[#888] font-bold mt-2">Letzter Login: {formatDateTime(organisation.lastLogin)}</p>
              </>
            )}
            {mode === 'edit' && <OrganisationEditor organisation={organisation} setReload={setReload} organisationId={organisationId} />}
            <div className="flex gap-4">
              <Button
                className="w-fit"
                onClick={() => setMode((prev) => (prev === 'view' ? 'edit' : 'view'))}
                variant={mode === 'view' ? 'default' : 'outline'}>
                {mode === 'view' ? 'Bearbeiten' : 'Abbrechen'}
              </Button>
              <Button type="submit" variant="destructive" className="text-white flex items-center gap-4 w-fit" onClick={resetAdminPassword}>
                <p>Admin Passwort zurücksetzen</p>
              </Button>
            </div>
          </div>
          {organisation.offerCheckoutData && <OfferCheckoutCard offer={organisation.offerCheckoutData} />}
          {organisation.selfCheckoutData && (
            <div className="flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px] w-1/4">
              <H4>Self-Checkout</H4>
              <>
                <div className="grid grid-cols-1">
                  <p className="text-[16px] text-[#888]">
                    <span className="font-bold">Paketname: </span>
                    {organisation.selfCheckoutData.packageName}
                  </p>
                  <p className="text-[16px] text-[#888]">
                    <span className="font-bold">Paketpreis: </span>
                    {calculatePackagePrice(organisation.selfCheckoutData.packagePrice)}
                  </p>
                  <p className="text-[16px] text-[#888]">
                    <span className="font-bold">Laufzeit: </span>
                    {organisation.selfCheckoutData.minimumContractDurationMonths}{' '}
                    {organisation.selfCheckoutData.minimumContractDurationMonths === 1 ? 'Monat' : 'Monate'}
                  </p>
                  <p className="text-[16px] text-[#888]">
                    <span className="font-bold">Frühzeitiger Opt-Out: </span>
                    {organisation.selfCheckoutData.earlyOptOut ? 'Ja' : 'Nein'}
                  </p>
                  <p className="text-[16px] text-[#888]">
                    <span className="font-bold">Testphase: </span>
                    {organisation.trialStartedAt
                      ? `${organisation.isTrialing ? 'Aktiv' : 'Inaktiv'} (${formatDateDetail(organisation.trialStartedAt ?? '')})`
                      : 'Keine Testphase'}
                  </p>
                  {(organisation.selfCheckoutData.additionalConditions || organisation.selfCheckoutData.additionalPriceConditions) && (
                    <>
                      <hr className="my-2"></hr>
                      <H5>Zusätzliche Bedingungen</H5>
                      <p className="text-[16px] text-[#888]">{organisation.selfCheckoutData.additionalConditions}</p>
                      <p className="text-[16px] text-[#888]">{organisation.selfCheckoutData.additionalPriceConditions}</p>
                    </>
                  )}
                </div>
              </>
            </div>
          )}
        </div>
        <div className="flex flex-row gap-5">
          <div
            className={`flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px] ${organisation.multipleAssignmentEnabled ? 'w-full' : 'w-1/2'}`}>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(updateStatus)} className="flex flex-col gap-3 h-full">
                <FormLabel className="text-[16px] font-bold text-[#12282A]">Status</FormLabel>
                <div className="w-1/2 flex flex-col gap-2">
                  <div className="flex gap-2">
                    <div className="grow w-1/2">
                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem className="flex flex-col bg-white rounded-lg">
                            <FormControl>
                              <Select
                                onValueChange={(value) => {
                                  field.onChange(value);
                                }}
                                defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger className="gap-3 h-10 rounded-2xl px-3 py-2 border text-sm inline-flex whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground">
                                    <SelectValue placeholder="Status wählen" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {organisation.selectableEntities?.status.map((status) => (
                                    <SelectItem key={status.id} value={status.id}>
                                      {status.value}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grow w-1/2">
                      {selectedUsesDate(form.watch('status')) ? (
                        <>
                          <FormField
                            control={form.control}
                            name="statusDate"
                            render={({ field }) => (
                              <FormItem className="flex flex-col bg-white rounded-lg">
                                <FormControl>
                                  <Input type="date" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                  <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit">
                    <p>Speichern</p>
                  </Button>
                </div>
              </form>
            </Form>
          </div>
          {/* A future capacity change would alter allowedJobsCount at a later point without anyone
              redistributing the shares, so it is not offered while multiple assignment is enabled. */}
          {!organisation.multipleAssignmentEnabled && (
            <div className="flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px] w-1/2">
              <OrganisationFutureCapacity organisation={organisation} organisationId={organisationId} setReload={setReload} />
            </div>
          )}
        </div>
        <div className="flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px]">
          <div className="flex items-center justify-between">
            <p className="text-[16px] font-bold text-[#12282A]">Benutzer</p>
            <AddOrganizationMemberForm organisationId={organisationId} setReload={setReload} />
          </div>
          <OrganisationUsers
            organizationAdmin={organisation.organizationAdmin}
            organizationUsers={organisation.organizationUsers}
            setReload={setReload}
            organisationId={organisationId}
          />
          <OrganisationInvitationsList organisationId={organisationId} setReload={setReload} />
        </div>
        <AssignmentCard organisation={organisation} organisationId={organisationId} setReload={setReload} />
      </fieldset>
    </div>
  );
};

const OrganisationFutureCapacity = ({
  organisation,
  organisationId,
  setReload
}: {
  organisation: OrganizationDetailsResponseDto;
  organisationId: string;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const formSchema = z.object({
    futureCapacity: z.number().min(1, {
      message: 'Future Capacity ist erforderlich'
    }),
    futureCapacityDate: z.string().min(1, {
      message: 'Future Capacity Datum ist erforderlich'
    })
  });

  type FormSchema = z.infer<typeof formSchema>;

  const getDateString = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  };

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      futureCapacity: organisation.futureCapacity ?? 0,
      futureCapacityDate: organisation.futureCapacityDate ? getDateString(organisation.futureCapacityDate) : ''
    }
  });

  const onSubmit = async (data: FormSchema) => {
    const requestData = {
      futureCapacity: data.futureCapacity,
      futureCapacityDate: data.futureCapacityDate ? new Date(data.futureCapacityDate).toISOString() : null
    };

    await apiRequest(`organizations/internal/${organisationId}/futureCapacity`, 'PATCH', {
      body: requestData,
      toast: {
        toastText: 'Zukünftige Kapazität erfolgreich aktualisiert'
      }
    });
    setReload((prev) => !prev);
  };

  const onDelete = async () => {
    await apiRequest(`organizations/internal/${organisationId}/futureCapacity`, 'DELETE', {
      toast: {
        toastText: 'Zukünftige Kapazität erfolgreich gelöscht'
      }
    });
    setReload((prev) => !prev);
  };

  // Check if future capacity data exists to show delete button
  const hasFutureCapacityData =
    organisation.futureCapacity !== null &&
    organisation.futureCapacity !== undefined &&
    organisation.futureCapacityDate !== null &&
    organisation.futureCapacityDate !== undefined;

  return (
    <div className="flex flex-col gap-[14px]">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 h-full">
          <FormLabel className="text-[16px] font-bold text-[#12282A]">Zukünftige Kapazität</FormLabel>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <div className="grow">
                <FormField
                  control={form.control}
                  name="futureCapacity"
                  render={({ field }) => (
                    <FormItem className="flex flex-col bg-white rounded-lg">
                      <FormLabel className="text-[16px] font-bold text-[#12282A]">Kapazität</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grow">
                <FormField
                  control={form.control}
                  name="futureCapacityDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col bg-white rounded-lg">
                      <FormLabel className="text-[16px] font-bold text-[#12282A]">Datum</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit">
                <p>Speichern</p>
              </Button>
              {hasFutureCapacityData && (
                <Button type="button" variant="destructive" className="text-white font-bold flex items-center gap-4 w-fit" onClick={onDelete}>
                  <p>Löschen</p>
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

const OrganisationEditor = ({
  organisation,
  organisationId,
  setReload
}: {
  organisation: OrganizationDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
  organisationId: string;
}) => {
  const { apiRequest } = useApiRequest();

  const formSchema = z.object({
    name: z.string().min(1, {
      message: 'Name ist erforderlich'
    }),
    debitReference: z.string().min(1, {
      message: 'Debitreferenz ist erforderlich'
    }),
    signatureDate: z.string().min(1, {
      message: 'Signaturdatum ist erforderlich'
    }),
    vatId: z.string().min(1, {
      message: 'USt-IdNr. ist erforderlich'
    }),
    street: z.string().min(1, {
      message: 'Straße ist erforderlich'
    }),
    houseNumber: z.string().min(1, {
      message: 'Hausnummer ist erforderlich'
    }),
    postCode: z.string().min(1, {
      message: 'Postleitzahl ist erforderlich'
    }),
    city: z.string().min(1, {
      message: 'Ort ist erforderlich'
    }),
    country: z.string().min(1, {
      message: 'Land ist erforderlich'
    }),
    invoiceMail: z.string().min(1, {
      message: 'Rechnungsmail ist erforderlich'
    }),
    allowedJobsCount: z.coerce.number().min(0, {
      message: 'Erlaubte Jobsanzahl ist erforderlich'
    }),
    iban: z.string().min(1, {
      message: 'IBAN ist erforderlich'
    }),
    bic: z.string().min(1, {
      message: 'BIC ist erforderlich'
    }),
    phone: z.string().min(1, {
      message: 'Telefon ist erforderlich'
    })
  });

  type FormSchema = z.infer<typeof formSchema>;

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: organisation.name,
      street: organisation.street,
      houseNumber: organisation.houseNumber,
      postCode: organisation.postCode,
      city: organisation.city,
      country: organisation.country,
      invoiceMail: organisation.invoiceMail,
      allowedJobsCount: organisation.allowedJobsCount,
      vatId: organisation.vatId,
      signatureDate: new Date(organisation.signatureDate).toISOString().split('T')[0],
      debitReference: organisation.debitReference,
      iban: organisation.iban,
      bic: organisation.bic,
      phone: organisation.phone
    }
  });

  const onSubmit = async (data: FormSchema) => {
    const req: UpdateOrganizationRequestDto = {
      name: data.name,
      street: data.street,
      houseNumber: data.houseNumber,
      postCode: data.postCode,
      city: data.city,
      country: data.country,
      invoiceMail: data.invoiceMail,
      allowedJobsCount: data.allowedJobsCount,
      vatId: data.vatId,
      signatureDate: data.signatureDate,
      debitReference: data.debitReference,
      iban: data.iban,
      bic: data.bic,
      phone: data.phone
    };

    await apiRequest(`organizations/internal/${organisationId}`, 'PATCH', {
      body: req,
      toast: {
        toastText: 'Organisation erfolgreich aktualisiert'
      }
    });
    setReload((prev) => !prev);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-7 h-full">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-3">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Name</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="allowedJobsCount"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Erlaubte Jobsanzahl</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="signatureDate"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Signaturdatum</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="debitReference"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Debitreferenz</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="vatId"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">USt-IdNr.</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="street"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Straße</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="houseNumber"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Hausnummer</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postCode"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">PLZ</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Ort</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Land</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="invoiceMail"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Rechnungsmail</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Telefon</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="iban"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">IBAN</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bic"
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">BIC</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} className="w-full h-10 px-3 text-[#12282A] border border-[#d1d5db] rounded-lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit self-end">
              <p>Speichern</p>
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

const OrganisationUsers = ({
  organizationAdmin,
  organizationUsers,
  organisationId,
  setReload
}: {
  organizationAdmin: InternalUserListResponseDto;
  organizationUsers: InternalUserListResponseDto[];
  organisationId: string;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const [user, setUser] = useState<InternalUserListResponseDto>();

  const getSelectedUser = (id: string) => {
    const user = [organizationAdmin, ...organizationUsers].find((user) => user._id === id);
    if (user) {
      setUser(user);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-4">
        {[organizationAdmin, ...organizationUsers].map((user, i) => (
          <div
            key={user._id}
            className={`cursor-pointer ${user.isArchive ? 'bg-gray-100' : ''}`}
            onClick={() => getSelectedUser(user._id)}
            style={{ borderRadius: '50px' }}>
            <div className="flex gap-2">
              <img
                src={user.profilePictureUrl ?? '/user-default.svg'}
                alt={user.firstName}
                width={54}
                height={54}
                className={`rounded-full h-14 w-14 object-cover ${user.isArchive ? 'opacity-50' : ''}`}
              />
              <div>
                <p className={`text-[16px] font-bold ${user.isArchive ? 'text-gray-500' : 'text-[#12282A]'}`}>
                  {user.firstName} {user.lastName}
                  {i === 0 ? ' - Admin' : ''}
                </p>
                <p className={`text-[16px] ${user.isArchive ? 'text-gray-400' : 'text-[#888]'}`}>{user.mail}</p>
              </div>
            </div>
            <hr />
          </div>
        ))}
      </div>
      {user && (
        <OrganisationUserEditor
          user={user}
          setReload={setReload}
          onClose={() => setUser(undefined)}
          endpoint={`organizations/internal/${organisationId}/user/${user._id}`}
        />
      )}
    </div>
  );
};

export default OrganisationDetailParent;
